import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy } from '@angular/router';

/**
 * Lets a handful of sibling routes (e.g. the Record page's /record, /url-meet, /history) share one
 * live component instance instead of the default behaviour of destroying and recreating it on every
 * navigation. Opt in per route via `data: { tabGroup: '<name>' }` — anything without that key falls
 * back to Angular's normal reuse rules, so the rest of the app is unaffected.
 *
 * This is what keeps an in-progress recording alive while the user is on the URL Meet or History
 * tab: those are real navigations to real URLs, and without this they would tear the page down.
 * A side effect of the router treating these as "reused" rather than "activated/deactivated": guards
 * and resolvers do not re-run between tabs in the same group, only when leaving the group entirely.
 */
@Injectable({ providedIn: 'root' })
export class TabGroupRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    const futureGroup = future.data?.['tabGroup'];
    const currGroup = curr.data?.['tabGroup'];

    if (futureGroup && futureGroup === currGroup) {
      return true;
    }

    return super.shouldReuseRoute(future, curr);
  }
}
